// src/globalConfigs/ModelFormConfigEditor.jsx
// Editor for modelFormConfig global configuration

import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Container,
  Title,
  Card,
  Stack,
  Button,
  Alert,
  Group,
  Text,
  JsonInput,
  Grid,
  Badge,
  Divider
} from '@mantine/core';
import { IconAlertCircle, IconCheck, IconDeviceFloppy } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { fetchGlobalConfig, saveGlobalConfig } from '../redux/actions/globalConfigActions';

export default function ModelFormConfigEditor() {
  const dispatch = useDispatch();
  const modelFormConfig = useSelector(s => s.globalConfigs?.configs?.modelFormConfig);
  const loading = useSelector(s => s.globalConfigs?.loading);
  const saving = useSelector(s => s.globalConfigs?.saving);

  const [json, setJson] = useState('');
  const [isValid, setIsValid] = useState(true);
  const [error, setError] = useState(null);

  // Fetch config on mount
  useEffect(() => {
    dispatch(fetchGlobalConfig('modelFormConfig'));
  }, [dispatch]);

  // Update JSON when config loads
  useEffect(() => {
    if (modelFormConfig) {
      setJson(JSON.stringify(modelFormConfig, null, 2));
      setIsValid(true);
      setError(null);
    }
  }, [modelFormConfig]);

  // Validate JSON on change
  const handleJsonChange = (value) => {
    setJson(value);
    try {
      JSON.parse(value);
      setIsValid(true);
      setError(null);
    } catch (err) {
      setIsValid(false);
      setError(err.message);
    }
  };

  // Save configuration
  const handleSave = async () => {
    try {
      const parsed = JSON.parse(json);

      // Validate structure
      if (!parsed.fields || !Array.isArray(parsed.fields)) {
        throw new Error('Config must have a "fields" array');
      }

      // Validate each field
      for (const field of parsed.fields) {
        if (!field.name || !field.type || !field.label) {
          throw new Error('Each field must have: name, type, and label');
        }
      }

      await dispatch(saveGlobalConfig(
        'modelFormConfig',
        parsed,
        'Defines the form fields for model registration in Model Inventory'
      ));

      notifications.show({
        title: 'Success',
        message: 'Model form configuration saved successfully',
        color: 'green',
        icon: <IconCheck />
      });
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: err.message,
        color: 'red',
        icon: <IconAlertCircle />
      });
    }
  };

  // Preview the current config
  const previewConfig = () => {
    try {
      const parsed = JSON.parse(json);
      return parsed.fields || [];
    } catch {
      return [];
    }
  };

  return (
    <Container size="xl" py="xl">
      <Stack spacing="lg">
        {/* Header */}
        <Group position="apart">
          <div>
            <Title order={2}>Model Form Fields Configuration</Title>
            <Text size="sm" color="dimmed" mt="xs">
              Configure the form fields displayed when registering a new model in the Model Inventory
            </Text>
          </div>
          <Badge size="lg" color={isValid ? 'green' : 'red'}>
            {isValid ? 'Valid JSON' : 'Invalid JSON'}
          </Badge>
        </Group>

        <Alert icon={<IconAlertCircle />} title="Global Configuration" color="blue">
          This configuration applies to ALL models across the organization. Changes here will affect the model registration form for all users.
        </Alert>

        <Grid>
          {/* JSON Editor */}
          <Grid.Col span={6}>
            <Card withBorder>
              <Stack>
                <Group position="apart">
                  <Text weight={500}>JSON Configuration</Text>
                  <Button
                    onClick={handleSave}
                    disabled={!isValid || saving}
                    loading={saving}
                    leftIcon={<IconDeviceFloppy size={16} />}
                  >
                    Save Configuration
                  </Button>
                </Group>

                <Divider />

                <JsonInput
                  label="Configuration JSON"
                  description="Define the fields for model registration form"
                  value={json}
                  onChange={handleJsonChange}
                  minRows={20}
                  maxRows={30}
                  formatOnBlur
                  autosize
                  error={error}
                  disabled={loading}
                />

                {error && (
                  <Alert color="red" icon={<IconAlertCircle />}>
                    <Text size="sm">{error}</Text>
                  </Alert>
                )}
              </Stack>
            </Card>
          </Grid.Col>

          {/* Preview */}
          <Grid.Col span={6}>
            <Card withBorder>
              <Stack>
                <Text weight={500}>Field Preview</Text>
                <Divider />

                {loading ? (
                  <Text color="dimmed">Loading...</Text>
                ) : previewConfig().length > 0 ? (
                  <Stack spacing="xs">
                    {previewConfig().map((field, idx) => (
                      <Card key={idx} withBorder p="sm" sx={{ background: '#f9fafb' }}>
                        <Group position="apart">
                          <div>
                            <Text size="sm" weight={500}>{field.label}</Text>
                            <Text size="xs" color="dimmed">
                              name: {field.name} | type: {field.type}
                              {field.required && ' | required'}
                            </Text>
                          </div>
                          <Badge size="xs" color={field.required ? 'red' : 'gray'}>
                            {field.required ? 'Required' : 'Optional'}
                          </Badge>
                        </Group>
                        {field.options && (
                          <Text size="xs" color="dimmed" mt="xs">
                            Options: {field.options.join(', ')}
                          </Text>
                        )}
                      </Card>
                    ))}
                  </Stack>
                ) : (
                  <Text color="dimmed">No fields defined</Text>
                )}
              </Stack>
            </Card>
          </Grid.Col>
        </Grid>

        {/* Documentation */}
        <Card withBorder>
          <Stack spacing="sm">
            <Text weight={500}>Field Structure</Text>
            <Text size="sm" color="dimmed">
              Each field in the configuration must have the following properties:
            </Text>
            <Stack spacing="xs" pl="md">
              <Text size="sm">• <strong>name</strong>: Unique identifier (camelCase)</Text>
              <Text size="sm">• <strong>type</strong>: Field type (text, textarea, select)</Text>
              <Text size="sm">• <strong>label</strong>: Display label for the field</Text>
              <Text size="sm">• <strong>required</strong>: (Optional) Boolean, whether field is required</Text>
              <Text size="sm">• <strong>options</strong>: (Required for select) Array of option values</Text>
            </Stack>
          </Stack>
        </Card>
      </Stack>
    </Container>
  );
}
